import { useState } from "react";
import dayjs from "dayjs";
import { CalendarView } from "./Calendar";
import clsx from "clsx";
import { db, eq } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { useSession } from "../lib/session";
import { useRefData } from "../lib/refdata";
import { navigate } from "../lib/router";
import { ErrorBox, Field, Panel, Select, Sheet, useToast, Empty, Loading } from "./ui";
import { openPerson } from "./PersonDrawer";
import { PageHead } from "./Shell";
import { Seg } from "./ui";
import { clock, zoneLabel, zoneLocal, zoneToUtc, type Zone } from "../lib/zones";
import { MeetingWhen, nextHourLocal } from "./MeetingWhen";

export type Appt = {
  id: string; person_id: string | null; title: string; starts_at: string; ends_at: string | null; meeting_url: string | null;
  location: string | null; status: "Scheduled" | "Proposed" | "Canceled" | "Completed" | "No-show"; zone_code?: Zone; source: "crm" | "calendly" | "review";
  invitee_name: string | null; invitee_email: string | null; remind: boolean; notes: string | null;
  person?: { full_name: string; email: string | null } | null;
};

const SELECT = "*,person:people(full_name,email)";
const z = (d: dayjs.Dayjs) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

/** A prefilled "add to Google Calendar" link. Once it's in Google Calendar, Calendly sees the time as busy. */
export function googleCalendarUrl(o: { title: string; start: string | dayjs.Dayjs; end?: string | dayjs.Dayjs | null; details?: string | null; location?: string | null; allDay?: boolean }) {
  const s = dayjs(o.start), e = o.end ? dayjs(o.end) : s.add(1, "hour");
  const dates = o.allDay ? `${s.format("YYYYMMDD")}/${s.add(1, "day").format("YYYYMMDD")}` : `${z(s)}/${z(e)}`;
  const q = new URLSearchParams({ action: "TEMPLATE", text: o.title, dates });
  if (o.details) q.set("details", o.details);
  if (o.location) q.set("location", o.location);
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

export function GoogleCalButton(props: Parameters<typeof googleCalendarUrl>[0] & { small?: boolean }) {
  return (
    <a className={clsx("btn", props.small && "small")} href={googleCalendarUrl(props)} target="_blank" rel="noopener noreferrer"
       title="Opens Google Calendar with this filled in. Save it there and Calendly will treat the time as busy.">
      Add to Google Calendar
    </a>
  );
}

const SOURCE: Record<Appt["source"], string> = { crm: "Booked in CRM", calendly: "Calendly", review: "Client review" };

export function AppointmentSheet({ appt, person, onClose }: { appt: Appt | null; person?: { id: string; full_name: string }; onClose: () => void }) {
  const toast = useToast();
  const defaultLink: string = useRefData().data?.setting("notifications")?.default_meeting_url ?? "";
  // times are typed on the meeting's province clock (Ontario unless you pick another)
  const zone0: Zone = appt?.zone_code ?? "ON";
  const start0 = appt ? zoneLocal(appt.starts_at, zone0) : nextHourLocal(zone0);
  const [f, setF] = useState({
    title: appt?.title ?? "Meeting", start: start0, zone: zone0,
    minutes: appt?.ends_at ? String(dayjs(appt.ends_at).diff(dayjs(appt.starts_at), "minute")) : "60",
    meeting_url: appt ? appt.meeting_url ?? "" : defaultLink, location: appt?.location ?? "", remind: appt?.remind ?? true,
    status: appt?.status ?? "Scheduled", notes: appt?.notes ?? "",
  });
  const [err, setErr] = useState<Error | null>(null);
  const [who, setWho] = useState<{ id: string; full_name: string } | null>(person ?? null);
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const found = useQuery(!appt && !person && dq.length > 1 ? `appt-find:${dq}` : null, () =>
    db.select<{ id: string; full_name: string; email: string | null; known_as: string | null }>("people", { select: "id,full_name,email,known_as", or: `(full_name.ilike.*${dq.replace(/[*,()]/g, " ")}*,known_as.ilike.*${dq.replace(/[*,()]/g, " ")}*)`, deleted_at: "is.null", limit: 8 }));
  const name = appt?.person?.full_name ?? appt?.invitee_name ?? who?.full_name ?? "";
  const readonlyTime = appt?.source === "calendly";
  // guests: get the same emails and reminders, with their own link to accept / reschedule / cancel
  type Guest = { id?: string; name: string; email: string };
  const saved = useQuery(appt ? `appts:guests:${appt.id}` : null, () => db.select<Guest>("appointment_guests", { appointment_id: eq(appt!.id), select: "id,name,email", order: "created_at" }));
  const [guests, setGuests] = useState<Guest[] | null>(null);
  const gl = guests ?? saved.data ?? [];
  const [ng, setNg] = useState({ name: "", email: "" });
  const addGuest = () => {
    const email = ng.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr(new Error("Enter the guest's email address.")); return; }
    if (gl.some((g) => g.email.toLowerCase() === email)) return;
    setGuests([...gl, { name: ng.name.trim(), email }]); setNg({ name: "", email: "" }); setErr(null);
  };
  const startIso = f.start ? zoneToUtc(f.start, f.zone) : "";
  const end = dayjs(startIso).add(Number(f.minutes) || 60, "minute");

  async function save() {
    setErr(null);
    try {
      if (!f.start) throw new Error("Choose a date and time.");
      if (!appt && !who) throw new Error("Choose who the meeting is with.");
      const body: any = {
        title: f.title || "Meeting", meeting_url: f.meeting_url || null, location: f.location || null, remind: f.remind,
        status: f.status, notes: f.notes || null,
      };
      if (!readonlyTime) { body.starts_at = startIso; body.ends_at = end.toISOString(); body.zone_code = f.zone; }
      let id = appt?.id;
      if (appt) await db.update("appointments", { id: eq(appt.id) }, body);
      else id = (await db.insert<{ id: string }>("appointments", { ...body, person_id: who?.id ?? null, source: "crm" }))[0]?.id;
      if (id && guests) {
        const before = saved.data ?? [];
        const gone = before.filter((b) => !guests.some((g) => g.id === b.id));
        if (gone.length) await db.remove("appointment_guests", { id: `in.(${gone.map((g) => g.id).join(",")})` });
        const added = guests.filter((g) => !g.id);
        if (added.length) await db.insert("appointment_guests", added.map((g) => ({ appointment_id: id, name: g.name, email: g.email })));
        invalidate(`appts:guests:${id}`);
      }
      invalidate("appts"); invalidate("today"); invalidate("cc");
      // put it in Google Calendar straight away (if connected), so Calendly sees the time as busy
      db.rpc("google_sync_now").then(() => invalidate("appts")).catch(() => undefined);
      // the client's confirmation / update / cancellation email goes now, not at the next 10-minute run
      if (!readonlyTime) db.rpc("meeting_emails_now").catch(() => undefined);
      toast(appt ? "Meeting saved" : f.status === "Proposed" ? `Time proposed${f.remind ? " · the client can accept it from their email" : ""}` : `Meeting booked${f.remind ? " · confirmation and reminders go out by email" : ""}`);
      onClose();
    } catch (e) { setErr(e as Error); }
  }

  return (
    <Sheet title={appt ? `${f.title}${name ? " · " + name : ""}` : `Book a meeting${name ? " with " + name : ""}`} onClose={onClose} footer={<>
      {appt?.person_id && <button className="btn ghost" onClick={() => openPerson(appt.person_id!)}>Open person</button>}
      <button className="btn primary" onClick={save}>{appt ? "Save" : "Book meeting"}</button>
    </>}>
      <ErrorBox error={err} />
      {!appt && !person && (who ? (
        <Field label="With"><div className="row-inline" style={{ margin: 0 }}><strong>{who.full_name}</strong><button className="btn small ghost" onClick={() => setWho(null)}>Change</button></div></Field>
      ) : (
        <Field label="With">
          <input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a client, lead or team member" />
          {(found.data ?? []).map((p) => (
            <button key={p.id} className="btn ghost small" style={{ justifyContent: "space-between" }} onClick={() => { setWho(p); setQ(""); }}>
              <span>{p.full_name}{p.known_as && <span className="known-as"> · {p.known_as}</span>}</span><span className="small muted">{p.email ?? ""}</span></button>
          ))}
          {dq.length > 1 && found.data?.length === 0 && <span className="small muted">No one called “{dq}”. Add them in People first.</span>}
        </Field>
      ))}
      {readonlyTime && <p className="small muted" style={{ marginTop: 0 }}>Booked through Calendly: change the time in Calendly and it updates here within 10 minutes.</p>}
      <div className="form-grid">
        <Field label="What" wide><input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Date, time and time zone" wide><MeetingWhen value={f.start} zone={f.zone} disabled={readonlyTime} onChange={(start, zone) => setF({ ...f, start, zone })} /></Field>
        {!readonlyTime && <div className="wide"><ConflictNote start={startIso ? zoneLocal(startIso, "ON") : f.start} minutes={f.minutes} exclude={appt?.id} /></div>}
        <Field label="Length"><Select disabled={readonlyTime} value={f.minutes} onChange={(v) => setF({ ...f, minutes: v || "60" })}
          options={["15", "30", "45", "60", "90", "120"].map((m) => ({ value: m, label: Number(m) < 60 ? `${m} min` : `${Number(m) / 60} h` }))} /></Field>
        <Field label="Zoom / meeting link" wide><input className="input" placeholder="https://zoom.us/j/…" value={f.meeting_url} onChange={(e) => setF({ ...f, meeting_url: e.target.value })} /></Field>
        <Field label="Or a place"><input className="input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
        {appt && <Field label="Status"><Select value={f.status} onChange={(v) => setF({ ...f, status: (v || "Scheduled") as Appt["status"] })} options={["Scheduled", "Proposed", "Completed", "No-show", "Canceled"]} /></Field>}
        {!appt && <label className="check wide" style={{ alignItems: "flex-start" }}><input type="checkbox" checked={f.status === "Proposed"} onChange={(e) => setF({ ...f, status: e.target.checked ? "Proposed" : "Scheduled" })} />
          <span>Propose this time<div className="small muted">The client gets a “Meeting proposed” email and presses Accept to confirm it. Reminders start once they accept.</div></span></label>}
        <Field label="Guests" wide>
          {gl.length > 0 && <div className="chips" style={{ marginBottom: 6 }}>{gl.map((g) => (
            <span key={g.email} className="chip">{g.name ? `${g.name} · ` : ""}{g.email}<button className="chip-x" aria-label={`Remove ${g.email}`} onClick={() => setGuests(gl.filter((x) => x.email !== g.email))}>×</button></span>))}</div>}
          <div className="row-inline" style={{ margin: 0 }}>
            <input className="input" style={{ maxWidth: 170 }} placeholder="Name" value={ng.name} onChange={(e) => setNg({ ...ng, name: e.target.value })} />
            <input className="input" type="email" placeholder="guest@email.com" value={ng.email} onChange={(e) => setNg({ ...ng, email: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGuest(); } }} />
            <button type="button" className="btn small" onClick={addGuest}>Add guest</button>
          </div>
          <span className="small muted">Guests get the same emails and reminders as {name || "the client"}, with their own link to accept, reschedule or cancel. Saved when you press {appt ? "Save" : "Book meeting"}.</span>
        </Field>
        <Field label="Notes" wide><textarea className="textarea" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      </div>
      <label className="switch-row">
        <input type="checkbox" checked={f.remind} onChange={(e) => setF({ ...f, remind: e.target.checked })} />
        <span>Email them reminders before the meeting <span className="muted small">(timing and wording in Settings → Emails & reminders)</span></span>
      </label>
    </Sheet>
  );
}

/** "Is that time free?": checks your Google Calendar (else CRM meetings). A warning only; you can still book. */
export function ConflictNote({ start, minutes = 60, exclude }: { start: string | null | undefined; minutes?: number | string; exclude?: string | null }) {
  const ds = useDebounced(start ?? "", 500);
  const s = ds ? dayjs(ds) : null;
  const ok = !!s && s.isValid() && s.isAfter(dayjs().subtract(1, "hour"));
  const q = useQuery(ok ? `busy:${s!.toISOString()}:${minutes}:${exclude ?? ""}` : null, () =>
    db.rpc<{ checked: "google" | "crm" | "none"; busy: { title: string; starts_at: string; ends_at: string | null }[] }>("calendar_conflicts",
      { p_start: s!.toISOString(), p_end: s!.add(Number(minutes) || 60, "minute").toISOString(), p_exclude: exclude ?? null }), { staleMs: 30_000 });
  if (!ok || !q.data) return null;
  const where = q.data.checked === "google" ? "your Google Calendar" : "the CRM's meetings";
  if (!q.data.busy.length) return <span className="small" style={{ color: "var(--ok, #1a7f4b)" }}>✓ Free in {where}</span>;
  return (
    <div className="warn-box small" role="status" style={{ background: "var(--amber-bg, #fff6e0)", border: "1px solid var(--amber, #e0a100)", borderRadius: 8, padding: "6px 10px" }}>
      <b>Heads up:</b> that time isn't free in {where}:{" "}
      {q.data.busy.map((b, i) => <span key={i}>{i ? "; " : ""}{b.title} {dayjs(b.starts_at).format("h:mm")}{b.ends_at ? `–${dayjs(b.ends_at).format("h:mm A")}` : ""}</span>)}.
      {" "}You can still book it.
    </div>
  );
}

/** Send meetings to Google Calendar now (the 10-minute job is the backup). */
export const syncGoogleNow = () => db.rpc("google_sync_now").then(() => invalidate("appts")).catch(() => undefined);

/** A meeting is over once its end time has passed (1 hour after the start if no end is set). */
export const apptEnd = (a: Appt) => (a.ends_at ? dayjs(a.ends_at) : dayjs(a.starts_at).add(60, "minute"));
const isOver = (a: Appt) => apptEnd(a).isBefore(dayjs());
const isNow = (a: Appt) => !isOver(a) && dayjs(a.starts_at).isBefore(dayjs());

function When({ a }: { a: Appt }) {
  const s = dayjs(a.starts_at);
  const day = s.isSame(dayjs(), "day") ? "Today" : s.isSame(dayjs().add(1, "day"), "day") ? "Tomorrow" : s.format("ddd, MMM D");
  return <span className="nowrap"><strong>{day}</strong> · {s.format("h:mm A")}</span>;
}

export function ApptRow({ a, onOpen }: { a: Appt; onOpen: (a: Appt) => void }) {
  return (
    <button className={clsx("appt-row", a.status !== "Scheduled" && "muted-row")} onClick={() => onOpen(a)}>
      <When a={a} />
      <span className="appt-who"><strong>{a.person?.full_name ?? a.invitee_name ?? "—"}</strong> <span className="muted">· {a.title}</span></span>
      <span className="appt-tags">
        {a.status === "Proposed" ? <span className="pill special" title="Waiting for the client to accept">Proposed</span> : a.status !== "Scheduled" && <span className="pill cold">{a.status}</span>}
        {a.status === "Scheduled" && isNow(a) && <span className="pill warm">Happening now</span>}
        {a.status === "Scheduled" && isOver(a) && <span className="pill alert" title="Open it and set Completed or No-show">Did it happen?</span>}
        {a.zone_code && a.zone_code !== "ON" && <span className="chip" title="The client's time">{clock(zoneLocal(a.starts_at, a.zone_code))} {zoneLabel(a.zone_code)}</span>}
        {a.meeting_url && <span className="chip">Zoom</span>}
        {(a as any).google_event_id && <span className="chip" title="On your Google Calendar, so Calendly treats the time as busy">Google ✓</span>}
        <span className="chip">{SOURCE[a.source]}</span>
        {a.remind && a.status === "Scheduled" && !isOver(a) && <span className="chip" title="Reminder emails on">🔔</span>}
      </span>
    </button>
  );
}

const useUpcoming = (days: number) => useQuery(`appts:upcoming:${days}`, () => db.select<Appt>("appointments", {
  select: SELECT, starts_at: `gte.${dayjs().startOf("day").toISOString()}`, and: `(starts_at.lt.${dayjs().add(days, "day").endOf("day").toISOString()})`,
  status: "in.(Scheduled,Proposed)", order: "starts_at",
}).then((rows) => rows.filter((a) => !isOver(a))), { staleMs: 60_000 });

/** Today page: meetings in the next 7 days */
export function UpcomingMeetings() {
  const { can } = useSession();
  const q = useUpcoming(7);
  const [open, setOpen] = useState<Appt | null>(null);
  if (!can("activities")) return null;
  return (
    <Panel title="Upcoming meetings" hint="Next 7 days · Calendly, booked reviews and meetings you book"
      actions={<><BookMeetingButton className="btn small" label="Book" /><button className="btn small ghost" onClick={() => navigate("/leads/meetings")}>All meetings</button></>}>
      {q.loading && !q.data ? <Loading rows={3} /> : (q.data ?? []).length === 0
        ? <div className="panel-body small muted">Nothing booked in the next 7 days.</div>
        : <div className="appt-list">{(q.data ?? []).map((a) => <ApptRow key={a.id} a={a} onOpen={setOpen} />)}</div>}
      {open && <AppointmentSheet appt={open} onClose={() => setOpen(null)} />}
    </Panel>
  );
}

/** Leads → Meetings */
export function MeetingsPage() {
  const [view, setView] = useState<"upcoming" | "past">("upcoming");
  const [mode, setMode] = useState<"list" | "calendar">(() => { try { return (localStorage.getItem("rca.meetings.view") as any) || "list"; } catch { return "list"; } });
  const pickMode = (v: "list" | "calendar") => { setMode(v); try { localStorage.setItem("rca.meetings.view", v); } catch { /* fine */ } };
  const [month, setMonth] = useState(() => dayjs().startOf("month"));
  const cal = useQuery(mode === "calendar" ? `appts:cal:${month.format("YYYY-MM")}` : null, () => db.select<Appt>("appointments", {
    select: SELECT, starts_at: `gte.${month.subtract(7, "day").toISOString()}`, and: `(starts_at.lt.${month.add(1, "month").add(14, "day").toISOString()})`, order: "starts_at", limit: 1000,
  }), { staleMs: 60_000 });
  // Upcoming = not finished yet (in progress counts); Past = its end time has passed
  const q = useQuery(`appts:list:${view}`, () => db.select<Appt>("appointments", {
    select: SELECT,
    starts_at: view === "upcoming" ? `gte.${dayjs().subtract(1, "day").toISOString()}` : `lt.${dayjs().toISOString()}`,
    order: view === "upcoming" ? "starts_at" : "starts_at.desc", limit: 250,
  }).then((rows) => rows.filter((a) => (view === "upcoming" ? !isOver(a) : isOver(a))).slice(0, 200)), { staleMs: 60_000 });
  const [open, setOpen] = useState<Appt | null>(null);
  const [booking, setBooking] = useState(false);
  const { can } = useSession();
  const rows = q.data ?? [];
  const groups = rows.reduce<Record<string, Appt[]>>((m, a) => { const k = dayjs(a.starts_at).format("dddd, MMMM D"); (m[k] ??= []).push(a); return m; }, {});
  return (
    <>
      <PageHead title="Meetings" sub="Calendly bookings (with their Zoom links), booked client reviews and meetings you book. Clients get reminder emails before each one."
        actions={can("activities", "edit") && <button className="btn primary" onClick={() => setBooking(true)}>Book a meeting</button>} />
      <div className="filters">
        <Seg value={mode} onChange={pickMode} options={[{ value: "list", label: "List" }, { value: "calendar", label: "Calendar" }]} />
        {mode === "list" && <Seg value={view} onChange={setView} options={[{ value: "upcoming", label: "Upcoming" }, { value: "past", label: "Past" }]} />}
      </div>
      <ErrorBox error={q.error || cal.error} />
      {mode === "calendar" ? <Panel><div className="panel-body"><CalendarView month={month} onMonth={setMonth}
        items={(cal.data ?? []).map((a) => ({ id: a.id, at: a.starts_at, title: a.person?.full_name ?? a.invitee_name ?? a.title, sub: a.title,
          tone: a.status === "Canceled" ? "muted" : a.status === "No-show" ? "alert" : a.source === "review" ? "gold" : "default" }))}
        onOpen={(id) => setOpen((cal.data ?? []).find((a) => a.id === id) ?? null)} /></div></Panel> :
      <Panel>
        {q.loading && !q.data ? <Loading rows={6} /> : rows.length === 0 ? (
          <Empty title={view === "upcoming" ? "No meetings booked" : "No past meetings"}>Book one from any person (Book meeting), or connect Calendly in Settings → Emails & reminders.</Empty>
        ) : Object.entries(groups).map(([day, list]) => (
          <div key={day} className="appt-day"><div className="appt-day-h">{day}</div>
            <div className="appt-list">{list.map((a) => <ApptRow key={a.id} a={a} onOpen={setOpen} />)}</div></div>
        ))}
      </Panel>}
      {open && <AppointmentSheet appt={open} onClose={() => setOpen(null)} />}
      {booking && <AppointmentSheet appt={null} onClose={() => setBooking(false)} />}
    </>
  );
}

/** A "Book meeting" button that opens the booking form, optionally for one person. */
export function BookMeetingButton({ person, className = "btn", label = "Book meeting" }: { person?: { id: string; full_name: string }; className?: string; label?: string }) {
  const { can } = useSession();
  const [open, setOpen] = useState(false);
  if (!can("activities", "edit")) return null;
  return <><button className={className} onClick={() => setOpen(true)}>{label}</button>{open && <AppointmentSheet appt={null} person={person} onClose={() => setOpen(false)} />}</>;
}

/** Person drawer: their meetings + book one */
export function PersonMeetings({ person }: { person: { id: string; full_name: string } }) {
  const q = useQuery(`appts:person:${person.id}`, () => db.select<Appt>("appointments", { select: SELECT, person_id: eq(person.id), order: "starts_at.desc", limit: 10 }));
  const [open, setOpen] = useState<Appt | null | "new">(null);
  const { can } = useSession();
  if (!can("activities")) return null;
  return (
    <div className="appt-person">
      <div className="sol-head"><strong>Meetings</strong>{can("activities", "edit") && <button className="btn small" onClick={() => setOpen("new")}>Book meeting</button>}</div>
      {(q.data ?? []).length === 0 ? <div className="small muted">None booked.</div>
        : <div className="appt-list">{(q.data ?? []).map((a) => <ApptRow key={a.id} a={a} onOpen={setOpen} />)}</div>}
      {open && <AppointmentSheet appt={open === "new" ? null : open} person={person} onClose={() => setOpen(null)} />}
    </div>
  );
}
